import React, { useState, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, useMediaQuery } from '@mui/material'

// Component Imports
import MailContentList from './MailContentList'
import MailDetails from './MailDetails'
import ComposeMail from './ComposeMail'

import { extractHtmlContent, isThreadIncoming } from '../../utils/helpers'

const FOLDERS = {
  ALL: 'all',
  INBOX: 'inbox',
  SENT: 'sent'
}

const MailContent = props => {
  const {
    fetching,
    isBelowSmScreen,
    emailArrays,
    activeEmailArr,
    setActiveEmailArr,
    reloadMessages,
    submitting,
    handleSubmitReply,
    onSubmitNewMail
  } = props

  const { t } = useTranslation()
  const isBelowLgScreen = useMediaQuery(theme => theme.breakpoints.down('lg'))

  // States
  const [searchTerm, setSearchTerm] = useState('')
  const [repliedMessage, setRepliedMessage] = useState(null)
  const [openCompose, setOpenCompose] = useState(false)
  const [activeFolder, setActiveFolder] = useState(FOLDERS.ALL)

  const folderCounts = useMemo(() => {
    const counts = { all: emailArrays.length, inbox: 0, sent: 0 }
    emailArrays.forEach(emailArr => {
      if (isThreadIncoming(emailArr)) counts.inbox++
      else counts.sent++
    })
    return counts
  }, [emailArrays])

  const filteredEmailArrays = useMemo(() => {
    return emailArrays.filter(emailArr => {
      const incoming = isThreadIncoming(emailArr)
      if (activeFolder === FOLDERS.INBOX && !incoming) return false
      if (activeFolder === FOLDERS.SENT && incoming) return false

      if (!searchTerm) return true

      const term = searchTerm.toLowerCase()
      return (
        emailArr?.[0]?.subject?.toLowerCase().includes(term) ||
        emailArr?.[0]?.from_name?.toLowerCase().includes(term) ||
        emailArr?.[0]?.from_address?.toLowerCase().includes(term) ||
        emailArr?.[0]?.to?.[0]?.name?.toLowerCase().includes(term) ||
        emailArr?.[0]?.to?.[0]?.address?.toLowerCase().includes(term) ||
        extractHtmlContent(emailArr?.[0]?.bodyHtml)?.toLowerCase().includes(term)
      )
    })
  }, [emailArrays, activeFolder, searchTerm])

  const showDetailPanel = !!activeEmailArr && !isBelowLgScreen
  const showDetailDrawer = !!activeEmailArr && isBelowLgScreen

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      {/* Email List Panel */}
      <Box sx={{
        width: showDetailPanel
          ? '440px'
          : '100%',
        minWidth: showDetailPanel
          ? '440px'
          : undefined,
        maxWidth: showDetailPanel
          ? '440px'
          : undefined,
        display: 'flex',
        flexDirection: 'column',
        borderRight: showDetailPanel
          ? '1px solid'
          : 'none',
        borderColor: 'divider',
        transition: 'width 0.2s ease',
        height: '100%'
      }}>
        <MailContentList
          fetching={fetching}
          filteredEmailArrays={filteredEmailArrays}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          emailArrays={emailArrays}
          activeEmailArr={activeEmailArr}
          setActiveEmailArr={setActiveEmailArr}
          setOpenCompose={setOpenCompose}
          reloadMessages={reloadMessages}
          activeFolder={activeFolder}
          setActiveFolder={setActiveFolder}
          folderCounts={folderCounts}
        />
      </Box>

      {/* Detail Panel */}
      {showDetailPanel && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <MailDetails
            inline
            activeEmailArr={activeEmailArr}
            setRepliedMessage={setRepliedMessage}
            onClose={() => setActiveEmailArr(null)}
          />
        </Box>
      )}

      {/* Detail Drawer */}
      {showDetailDrawer && (
        <MailDetails
          inline={false}
          activeEmailArr={activeEmailArr}
          setRepliedMessage={setRepliedMessage}
          onClose={() => setActiveEmailArr(null)}
        />
      )}

      <ComposeMail
        title={t('mailTemplate.mailbox.send.replyTitle')}
        openCompose={!!repliedMessage}
        setOpenCompose={setRepliedMessage}
        isBelowSmScreen={isBelowSmScreen}
        onSubmit={handleSubmitReply}
        submitting={submitting}
        initialEmail={{
          ...repliedMessage,
          subject: `Re: ${repliedMessage?.subject}`,
          html: '<blockquote class="RichTextEditorTheme__quote" dir="ltr">' + repliedMessage?.bodyHtml + '</blockquote>'
        }}
        focusSubjectAfterInit
        replyMessageId={repliedMessage?.messageId}
      />

      <ComposeMail
        title={t('mailTemplate.mailbox.send.composeTitle')}
        openCompose={!!openCompose}
        setOpenCompose={setOpenCompose}
        isBelowSmScreen={isBelowSmScreen}
        onSubmit={onSubmitNewMail}
        submitting={submitting}
      />
    </Box>
  )
}

export default MailContent
